import React, { useState, useEffect } from "react";
import { X, Upload, FileText } from "lucide-react";
import { ExternalAgency } from "../../../types/api"; 
import { useExternalAgenciesStore } from "../../../store/store";
import { useS3Upload } from "../../../utils/s3Client";
import { toast } from "react-toastify";

interface CreateExternalAgencyProps {
  onClose: () => void;
  onSave: () => void;
  agencyToEdit?: ExternalAgency | null;
  isEdit?: boolean;
}

const CreateExternalAgency: React.FC<CreateExternalAgencyProps> = ({
  onClose,
  onSave,
  agencyToEdit,
  isEdit = false,
}) => {
  const { createAgency, updateAgency } = useExternalAgenciesStore();
  const [formData, setFormData] = useState<Partial<ExternalAgency>>({
    agencyName: "",
    category: "",
    contactPerson: "",
    contactNumber: "",
    email: "",
    status: "Active",
    mouContractName: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { uploadFileToS3, uploading } = useS3Upload();

  useEffect(() => {
    if (isEdit && agencyToEdit) {
      setFormData(agencyToEdit);
    } else {
        setFormData({
            agencyName: "",
            category: "",
            contactPerson: "",
            contactNumber: "",
            email: "",
            status: "Active",
            mouContractName: "",
            mouContractExpiryDate: "",
        });
    }
  }, [isEdit, agencyToEdit]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size exceeds 5MB limit");
        return;
      }
      setSelectedFile(file);
      setFormData((prev) => ({
        ...prev,
        mouContractName: file.name, 
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
        let fileUrl = formData.mouContractName;

        if (selectedFile) {
            const uploadedUrl = await uploadFileToS3(selectedFile);
            if (!uploadedUrl) {
                throw new Error("File upload failed");
            }
            fileUrl = uploadedUrl;
        }

        const payload = {
            ...formData,
            mouContractName: fileUrl
        };

        if (isEdit && agencyToEdit && (agencyToEdit._id || agencyToEdit.id)) {
            // Prefer _id for MongoDB if available, else id
            await updateAgency((agencyToEdit._id || agencyToEdit.id)!, payload);
            toast.success("Agency updated successfully");
        } else {
            await createAgency(payload);
            toast.success("Agency created successfully");
        }
        onSave();
    } catch (error) {
        console.error("Failed to save agency:", error);
        toast.error("Failed to save agency. Please try again.");
    } finally {
        setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-lg w-full max-w-6xl mx-auto">
      <div className="flex justify-between items-center p-6 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800">
          {isEdit ? "Edit Agency Details" : "Add New External Agency"}
        </h2>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X size={20} className="text-gray-500" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Agency Name */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Agency Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="agencyName"
              value={formData.agencyName}
              onChange={handleChange}
              placeholder="Enter agency name"
              required
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
            />
          </div>

          {/* Category */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all bg-white"
            >
              <option value="">Select Category</option>
              <option value="Hospital">Hospital</option>
              <option value="Fire Brigade">Fire Brigade</option>
              <option value="Police">Police</option>
              <option value="Ambulance Service">Ambulance Service</option>
              <option value="Disaster Management">Disaster Management</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Contact Person */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Contact Person <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="contactPerson"
              value={formData.contactPerson}
              onChange={handleChange}
              placeholder="Enter contact person name"
              required
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
            />
          </div>

          {/* Contact Number */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Contact Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="contactNumber"
              value={formData.contactNumber}
              onChange={handleChange}
              placeholder="Enter contact number"
              required
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
            />
          </div>

          {/* Email */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter email address"
              required
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
            />
          </div>

          {/* Status */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all bg-white"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          {/* MOU / Contract Upload */}
          <div className="flex flex-col gap-2 md:col-span-3">
            <label className="text-sm font-medium text-gray-700">
              MOU / Contract Document
            </label>
            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-all">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  {formData.mouContractName ? (
                    <>
                      <FileText className="w-8 h-8 text-teal-500 mb-2" />
                      <p className="text-sm text-gray-500 font-medium">
                        {formData.mouContractName}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Click to replace
                      </p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500">
                        <span className="font-semibold">Click to upload</span>{" "}
                        or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">
                        PDF, DOC (MAX. 5MB)
                      </p>
                    </>
                  )}
                </div>
                <input
                  type="file"
                  className="hidden"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx"
                />
              </label>
            </div>
          </div>

          {/* MOU / Contract Expiry Date */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              MOU / Contract Expiry Date
            </label>
            <input
              type="date"
              name="mouContractExpiryDate"
              value={formData.mouContractExpiryDate || ""}
              onChange={handleChange}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
            />
          </div>
        </div>
      </form>

      <div className="p-6 border-t border-gray-200 flex justify-end gap-3 bg-gray-50 rounded-b-xl">
        <button
          onClick={onClose}
          className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="px-6 py-2.5 text-sm font-medium text-white bg-[#0fa9a6] rounded-lg hover:bg-[#0caba8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0fa9a6] transition-all shadow-sm disabled:opacity-50"
        >
          {submitting || uploading ? "Saving..." : isEdit ? "Update Agency" : "Add Agency"}
        </button>
      </div>
    </div>
  );
};

export default CreateExternalAgency;
